import { useRef } from 'react';

import Layout from '@/components/Layout/Vanilla';
import { scrollTo } from '@/lib/scroll';
import Heading from '@/components/renderers/Heading';
import HubSpotForm from '@/components/HubSpotForm';
import Button from '@/components/Button';
import ShowcaseCard from '@/components/sections/community/showcase';
import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';
import { fetchPage, gql } from '@/lib/graphcms';

export default function Showcase({ page, showcases, hubspotForm }) {
  const formRef = useRef(null);
  const { title, subTitle } = page;

  useIsomorphicLayoutEffect(() => {
    document.querySelector('body').classList.add('dark-page');

    return () => {
      document.querySelector('body').classList.remove('dark-page');
    };
  }, []);

  return (
    <>
      <Layout seo={page} removeSalesBreakpoint>
        <div className="max-w-container mx-auto px-6">
          <section className="mt-30 text-center max-w-3xl mx-auto">
            <Heading className="mt-0">{title}</Heading>
            <p className="text-lg leading-7 mt-6">{subTitle}</p>

            <Button
              onClick={() => scrollTo(formRef, -20)}
              colour="pink"
              className="mt-10"
            >
              Submit your project
            </Button>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16 mt-20">
            {showcases.map(({ id, ...rest }) => (
              <ShowcaseCard key={id} {...rest} />
            ))}
          </div>

          <div className="my-40" ref={formRef}>
            <h2
              className="font-bold text-3xl md:text-4xl lg:text-5xl leading-tight text-center"
              id="submit-your-project"
            >
              Submit your project
            </h2>

            <div className="mt-16 max-w-xl mx-auto">
              <HubSpotForm
                form={hubspotForm}
                buttonText="Submit project"
                buttonColour="pink"
                dark
              />
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}

export const getStaticProps = async ({ preview = false }) => {
  const { page, showcases } =
    (await fetchPage(
      gql`
        query showcasePageQuery {
          page: page(where: { slug: "showcase" }) {
            ...pageData
          }
          showcases: showcases {
            id
            authors {
              id
              name
              country
              photo {
                url(
                  transformation: {
                    image: { resize: { width: 40, height: 40, fit: clip } }
                  }
                )
                width
                height
                alt
              }
              gitHub
              twitterHandle
            }
            coverImage {
              url(
                transformation: {
                  image: { resize: { width: 744, height: 548, fit: clip } }
                }
              )
              width
              height
              alt
            }
            title
            externalUrl
            tags
            description
          }
        }
      `,
      {
        preview,
      },
    )) || [];

  const response = await fetch(
    `https://api.hubapi.com/forms/v2/forms/15164e76-ba40-40ca-bfc8-c092ea380a9e/?hapikey=${process.env.HUBSPOT_API_KEY}`,
  );

  const hubspotForm = await response.json();

  return {
    props: {
      preview,
      page,
      showcases,
      hubspotForm,
    },
    revalidate: 3600,
  };
};
